// src/pages/CategoryPage.jsx
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { Helmet } from 'react-helmet';
import { NavLink, useNavigate, useParams } from 'react-router-dom';
import apiClient, { IMAGE_BASE_URL } from '../../utils/api-client';
import CardSkeleton from '../Common/CardSkeleton';
import PageHeader from '../Common/PageHeader';
import CategoryProductList from '../Shop/CategoryProductsList';
import './ShopPage.css';

const CategoryPage = () => {
  const navigate = useNavigate();
  const { slug } = useParams();

  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState({});
  const [selectedSubcategory, setSelectedSubcategory] = useState(null);
  const [subcategoryProducts, setSubcategoryProducts] = useState(null);
  const [loadingProducts, setLoadingProducts] = useState(true);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [showLeftIcon, setShowLeftIcon] = useState(false);
  const [showRightIcon, setShowRightIcon] = useState(false);
  const scrollRef = useRef(null);

  const checkScroll = () => {
    const el = scrollRef.current;
    if (!el) return;
    setShowLeftIcon(el.scrollLeft > 0);
    setShowRightIcon(el.scrollLeft + el.clientWidth < el.scrollWidth);
  };

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;

    checkScroll(); // initial check

    el.addEventListener('scroll', checkScroll);
    window.addEventListener('resize', checkScroll);

    return () => {
      el.removeEventListener('scroll', checkScroll);
      window.removeEventListener('resize', checkScroll);
    };
  }, []);

  const scroll = (direction) => {
    const el = scrollRef.current;
    if (!el) return;

    const scrollAmount = 200;
    el.scrollBy({
      left: direction === 'left' ? -scrollAmount : scrollAmount,
      behavior: 'smooth',
    });
  };

  // Fetch category and all categories on slug change
  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const [categoryRes, allCategoriesRes] = await Promise.all([
          apiClient.get(`/category/${slug}`),
          apiClient.get('/category'),
        ]);

        setSelectedCategory(categoryRes.data.data);
        setCategories(allCategoriesRes.data.data);
        setSubcategoryProducts(null);
        setIsLoading(false);
      } catch (err) {
        setError(err.message);
        setIsLoading(false);
      }
    };

    fetchCategories();
  }, [slug]);

  // Fetch subcategory products
  useEffect(() => {
    if (selectedSubcategory) {
      setLoadingProducts(true);
      apiClient
        .get(`/subcategory/${selectedSubcategory}`)
        .then((res) => {
          setSubcategoryProducts(res.data.data);
          setLoadingProducts(false);
        })
        .catch((err) => {
          setError(err.message);
          setLoadingProducts(false);
        });
    } else {
      setLoadingProducts(false);
    }
  }, [selectedSubcategory]);

  const handleSubcategoryClick = (subcategorySlug) => {
    setSelectedSubcategory(subcategorySlug);
    navigate(`/${slug}/${subcategorySlug}/`);
  };

  return (
    <>
      <Helmet>
        <title>{selectedCategory.meta_title}</title>
        <meta name="description" content={selectedCategory.meta_desc} />
        <meta name="keywords" content={selectedCategory.keywords} />
      </Helmet>

      <PageHeader title="Shop" />

      <section className="append_products">
        <div className="container-fluid">
          <div className="row">
            {/* Subcategory Grid */}
            <div className="position-relative px-2">
              {/* Scroll Left Icon */}
              {showLeftIcon && (
                <button
                  className="scroll-btn left "
                  onClick={() => scroll('left')}
                  aria-label="Scroll Left"
                >
                  <ChevronLeft size={24} />
                </button>
              )}

              {/* Scrollable Subcategories */}
              <div className="col-12 overflow-hidden">
                <div
                  ref={scrollRef}
                  className="subcategory-scroll d-flex pb-2 px-2"
                >
                  {selectedCategory.subcategory?.length > 1 &&
                    selectedCategory.subcategory.map((subcategory, index) => (
                      <NavLink
                        key={index}
                        className="categories_list text-decoration-none"
                        to={`/category/${slug}/${subcategory.s_cat_slug}`}
                        onClick={() =>
                          handleSubcategoryClick(subcategory.s_cat_slug)
                        }
                      >
                        <div className="d-flex flex-column align-items-center text-center">
                          <img
                            src={`${IMAGE_BASE_URL}/${subcategory.s_cat_image}`}
                            alt={subcategory.p_sub_category_name}
                          />
                          <h5
                            title={subcategory.p_sub_category_name}
                            className="text-truncate"
                            style={{
                              maxWidth:
                                window.innerWidth > 578 ? '100px' : '80px',
                            }}
                          >
                            {subcategory.p_sub_category_name}
                          </h5>
                        </div>
                      </NavLink>
                    ))}
                </div>
              </div>

              {/* Scroll Right Icon */}
              {showRightIcon && (
                <button
                  className="scroll-btn right"
                  onClick={() => scroll('right')}
                  aria-label="Scroll Right"
                >
                  <ChevronRight size={24} />
                </button>
              )}
            </div>

            {/* Product Section */}
            <div className="col-12">
              {loadingProducts ? (
                <CardSkeleton cards={4} />
              ) : (
                <CategoryProductList
                  productsData={
                    subcategoryProducts ? subcategoryProducts : selectedCategory
                  }
                  isLoading={isLoading}
                  error={error}
                />
              )}
            </div>
            <hr />
            {/* Description Section */}
            {(selectedCategory.pcat_subtitle ||
              selectedCategory.pcat_short_desc) && (
              <div className="col-12">
                <div className="col-md-12 mt-3">
                  <h3 className="scat_subtitle">
                    {selectedCategory.pcat_subtitle}
                  </h3>
                  <div
                    className="scat_long_desc pt-3"
                    dangerouslySetInnerHTML={{
                      __html: selectedCategory.pcat_short_desc,
                    }}
                  />
                </div>
              </div>
            )}
          </div>
        </div>
      </section>
    </>
  );
};

export default CategoryPage;
